import authReducer from "./slices/authSlice.ts";
import userReducer from "./slices/userInfoStore.ts";
import metricsReducer from "./slices/metricsSlice.ts";
import createSagaMiddleware from "redux-saga";
import storage from "redux-persist/lib/storage";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import { combineReducers, Reducer } from "redux";
import { persistReducer, persistStore } from "redux-persist";
import { configureStore } from "@reduxjs/toolkit";
import rootSaga from "./sagas/rootSaga.ts";
import { RootReducerState } from "../utils/types.ts";

const sagaMiddleware = createSagaMiddleware();

const nativeReducers = {
  authReducer: authReducer,
  user: userReducer,
  metrics: metricsReducer,
};

const persistConfig = {
  key: "root",
  storage,
  stateReconciler: autoMergeLevel2,
  // authReducer is deliberately NOT persisted: auth status must be derived
  // from the real Cognito session on every boot (App.tsx bootstrap), never
  // rehydrated from a previous page's state.
  whitelist: ["user", "metrics"],
};

// Rebuilds the (persist-wrapped) root reducer from the native reducers plus
// whatever federated reducers have been injected so far. Re-wrapping with
// persistReducer on each injection is the pattern redux-persist documents for
// reducer replacement; only whitelisted (native) slices are persisted, so the
// late-arriving federated slices don't interact with rehydration.
const buildRootReducer = (federatedReducers: Record<string, Reducer>) =>
  persistReducer<RootReducerState>(
    persistConfig,
    combineReducers({
      ...nativeReducers,
      ...federatedReducers,
    }) as unknown as Reducer<RootReducerState>,
  );

// The store is created synchronously with only the portal's native reducers,
// so the app can render — and Amplify auth events can dispatch — without
// waiting for the federated remotes to download. Remote reducers/sagas are
// injected into the live store by loadFederatedPlugins() below.
export const store = configureStore({
  reducer: buildRootReducer({}),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ thunk: false }).concat(sagaMiddleware),
});

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);

// ---------------------------------------------------------------------------
// Federated plugin loading
// ---------------------------------------------------------------------------

// import() specifiers must stay literal so webpack Module Federation can
// resolve them against the remotes declared in craco.config.js.
const FEDERATED_PLUGINS: Array<{ name: string; load: () => Promise<any> }> = [
  { name: "orderManagement", load: () => import("orderManagement/plugin") },
  { name: "rateShop", load: () => import("rateShop/plugin") },
  { name: "invoiceManagement", load: () => import("invoiceManagement/plugin") },
];

// The portal owns the `user` slice; every remote also exports one, and theirs
// must never replace ours.
const REDUCER_BLACKLIST = new Set(["user"]);

const injectedReducers: Record<string, Reducer> = {};

const injectPlugin = (name: string, plugin: any) => {
  const reducers = (plugin?.reducers ?? {}) as Record<string, Reducer>;
  for (const [key, reducer] of Object.entries(reducers)) {
    if (!REDUCER_BLACKLIST.has(key)) {
      injectedReducers[key] = reducer;
    }
  }
  store.replaceReducer(buildRootReducer(injectedReducers));

  if (plugin?.rootSaga) {
    sagaMiddleware.run(plugin.rootSaga);
    console.log(
      `✅ ${name}/plugin loaded: reducers injected, rootSaga started`,
    );
  } else {
    console.warn(`${name}/plugin loaded but no rootSaga was exported`);
  }
};

// Downloads the three remotes in parallel and wires each into the live store
// as it arrives. Failures are isolated per plugin: a dead remote logs an error
// but never blocks the portal (or the other remotes) from working.
export const loadFederatedPlugins = async (): Promise<void> => {
  await Promise.allSettled(
    FEDERATED_PLUGINS.map(async ({ name, load }) => {
      try {
        const plugin = await load();
        injectPlugin(name, plugin);
      } catch (err) {
        console.error(`Failed to load ${name}/plugin:`, err);
      }
    }),
  );
};
